/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useState } from 'react';
import { ViewTemplatesButton } from '@jira/polaris__component-view-template-library/src/ViewTemplatesButton.tsx';
import {
	VIEW_KIND_MATRIX,
	VIEW_KIND_TIMELINE,
	VIEW_KIND_TABLE,
	VIEW_KIND_BOARD,
	VIEW_KIND_TREE,
} from '@jira/polaris__domain-view/src/viewKindTimeline.constants.tsx';
import { isFakeId } from '@jira/polaris__component-navigation-store/src/updateContainerState.tsx';
import { useNavigationActions } from '@jira/polaris__component-navigation-store/src/useNavigationActions.tsx';
import { useContainerViewsCount } from '@jira/polaris__component-navigation-store/src/useIsNavigationLocked.tsx';
import { useManageViewPermissions } from '@jira/polaris__component-permissions-store/src/useCanEditIssues.tsx';
import { useCanCreateFields } from '@jira/polaris__component-permissions-store/src/useCanCreateFields.tsx';
import { useCollectionsActions } from '@jira/polaris__component-collections/src/useCollectionsActions.tsx';
import { ViewBlueprintsTriggerButton } from '@jira/polaris__component-view-blueprints/src/ViewBlueprintsTriggerButton.tsx';
import { ViewBlueprintsButtonGuard } from '@jira/polaris__component-view-blueprints/src/ViewBlueprintsButtonGuard.tsx';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import ButtonItem from '@atlaskit/menu/button-item';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import type { MoreNavMenuButtonMenuItemsProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/submenu/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { isNavigationProject } from '../../../../../../common/types.tsx';
import {
	DELETE_VIEW,
	EDIT_GLOBAL_VIEW,
	RENAME_VIEW,
	REMOVE_EMOJI,
	CLONE_VIEW,
	type ItemId,
} from '../../../../../../common/constants/analytics/item-id.tsx';
import type { MenuProps } from '../../types.tsx';
import { DeleteModal } from '../../../../../delete-modal/index.tsx';
import { HardLimitsTooltip } from '../../../../../hard-limits-tooltip/index.tsx';
import {
	useAnalyticsL3,
	useAnalyticsL4,
	useIsReadOnly,
	useContainerTypePermissions,
	useViewsEntityLimitType,
	useContainerId,
} from '../../../../../../controllers/context.tsx';
import { useIsViewKindLocked } from '../../../../../../common/utils/useIsViewKindLocked.tsx';

const renameAction = defineMessage({
	defaultMessage: 'Rename',
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.rename-action',
	description: 'Label for the button to rename the selected view.',
});

const removeEmoji = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.remove-emoji',
	defaultMessage: 'Remove emoji',
	description: 'Label for removing emoji from selected view title',
});

const saveAsNewList = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.save-as-new-list',
	defaultMessage: 'Save as new list',
	description: 'Label for creating a new list view from the selected view.',
});

const saveAsNewSpaceList = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.save-as-new-space-list',
	defaultMessage: 'Save as new space list',
	description: 'Label for creating a new list view from the selected view.',
});

const saveAsNewBoard = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.save-as-new-board',
	defaultMessage: 'Save as new board',
	description: 'Label for creating a new board view from the selected view.',
});

const saveAsNewSpaceBoard = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.save-as-new-space-board',
	defaultMessage: 'Save as new space board',
	description: 'Label for creating a new board view from the selected view.',
});

const saveAsNewMatrix = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.save-as-new-matrix',
	defaultMessage: 'Save as new matrix',
	description: 'Label for creating a new matrix view from the selected view.',
});

const saveAsNewSpaceMatrix = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.save-as-new-space-matrix',
	defaultMessage: 'Save as new space matrix',
	description: 'Label for creating a new matrix view from the selected view.',
});

const saveAsNewTimeline = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.save-as-new-timeline',
	defaultMessage: 'Save as new timeline',
	description: 'Label for creating a new timeline view from the selected view.',
});

const saveAsNewSpaceTimeline = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.save-as-new-space-timeline',
	defaultMessage: 'Save as new space timeline',
	description: 'Label for creating a new timeline view from the selected view.',
});

const saveAsNewTree = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.save-as-new-tree',
	defaultMessage: 'Save as new tree',
	description: 'Label for creating a new tree view from the selected view.',
});

const deleteAction = defineMessage({
	defaultMessage: 'Delete',
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.delete-action',
	description: 'Label for deleting the selected view. Displayed in the sidebar.',
});

const managedByAdmin = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.managed-by-admin',
	defaultMessage: 'Managed by admin',
	description:
		'Text shown in the navigation view menu to indicate that the view is a global one, meaning it can only be edited by admins',
});

const editGlobalViewMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.edit-global-view',
	defaultMessage: 'Edit global view',
	description: 'Link button that takes the user to edit the current global view',
});

type NavMenuItemsProps = MoreNavMenuButtonMenuItemsProps & {
	onDelete: () => void;
	onTrack: (itemId: ItemId) => void;
	view: MenuProps['view'];
	navigationContainer: MenuProps['navigationContainer'];
};
const styles = cssMap({
	adminNoticeContainer: {
		paddingInline: token('space.200'),
		paddingBlock: token('space.200'),
		backgroundColor: token('color.background.neutral'),
		borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
	},
});

const NavMenuItems = ({
	onClose,
	onDelete,
	onTrack,
	view,
	navigationContainer,
}: NavMenuItemsProps) => {
	const containerId = useContainerId();
	const { renameView, cloneView, deleteEmoji } = useNavigationActions();
	const containerTypePermissions = useContainerTypePermissions();
	const { canManageView, canSeeView, canManageViews } = useManageViewPermissions(
		containerTypePermissions === 'view' ? view.id : containerId,
	);
	const isAdmin = useIsAdmin();

	const isReadOnly = useIsReadOnly();
	const viewsEntityLimitType = useViewsEntityLimitType();
	const containerViewsCount = useContainerViewsCount({ containerId });
	const { formatMessage } = useIntl();
	const isViewKindLocked = useIsViewKindLocked();
	const isViewLocked = isViewKindLocked(view.viewType);
	const isGlobalView = Boolean(view.global) && fg('polaris_global_views');

	const canCreateFields = useCanCreateFields();
	const isViewTemplateLibraryEnabled =
		canCreateFields &&
		isNavigationProject(navigationContainer) &&
		(expVal('view-template-library-m1-for-new-tenants', 'isEnabled', false) ||
			expVal('view-template-library-m1-for-existing-tenants', 'isEnabled', false));
	const isBlueprintsButtonVisible =
		!isViewTemplateLibraryEnabled && fg('jpd-view-templates-sample-project');

	const handleViewTemplatesClick = useCallback(() => {
		onClose?.(null);
	}, [onClose]);

	const isLastView = containerViewsCount === 1;
	const canRenameView = fg('jpd_tree-view') ? canManageView && !isViewLocked : canManageView;
	const canDeleteView = canManageView && !isReadOnly && !isLastView;
	const canRemoveEmoji =
		!isGlobalView &&
		(fg('jpd_tree-view') ? Boolean(view.emoji) && !isViewLocked : Boolean(view.emoji));
	const canSaveAsNewBoard = fg('jpd_tree-view')
		? [VIEW_KIND_TABLE, VIEW_KIND_BOARD].includes(view.viewType)
		: view.viewType !== VIEW_KIND_MATRIX && view.viewType !== VIEW_KIND_TIMELINE;
	const canSaveAsNewMatrix = view.viewType === VIEW_KIND_MATRIX;
	const canSaveAsNewTimeline = view.viewType === VIEW_KIND_TIMELINE;
	const canClone =
		containerTypePermissions === 'view'
			? canManageViews && canSeeView && !isReadOnly
			: canManageView && canSeeView && !isReadOnly;

	const canSaveAsNewList = view.viewType !== VIEW_KIND_TREE;

	const canSaveAsNewTree = view.viewType === VIEW_KIND_TREE && !isViewLocked;

	const handleRename = useCallback(() => {
		onTrack(RENAME_VIEW);
		renameView(view.localId);
	}, [onTrack, renameView, view.localId]);

	const handleRemoveEmoji = useCallback(() => {
		onTrack(REMOVE_EMOJI);
		deleteEmoji(view.localId);
	}, [deleteEmoji, onTrack, view.localId]);

	const handleCloneTable = useCallback(() => {
		onTrack(CLONE_VIEW);
		cloneView(view.localId, VIEW_KIND_TABLE);
	}, [cloneView, onTrack, view.localId]);

	const handleCloneBoard = useCallback(() => {
		onTrack(CLONE_VIEW);
		cloneView(view.localId, VIEW_KIND_BOARD);
	}, [cloneView, onTrack, view.localId]);

	const handleCloneMatrix = useCallback(() => {
		onTrack(CLONE_VIEW);
		cloneView(view.localId, VIEW_KIND_MATRIX);
	}, [cloneView, onTrack, view.localId]);

	const handleCloneTimeline = useCallback(() => {
		onTrack(CLONE_VIEW);
		cloneView(view.localId, VIEW_KIND_TIMELINE);
	}, [cloneView, onTrack, view.localId]);

	const handleCloneTree = useCallback(() => {
		onTrack(CLONE_VIEW);
		cloneView(view.localId, VIEW_KIND_TREE);
	}, [cloneView, onTrack, view.localId]);

	const handleDelete = useCallback(() => {
		onTrack(DELETE_VIEW);
		onDelete();
	}, [onDelete, onTrack]);

	const handleEditGlobalView = useCallback(() => {
		onTrack(EDIT_GLOBAL_VIEW);
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.open(
			`/jira/settings/products/jira-product-discovery/global-views/${view.globalViewUuid}`,
			'_blank',
			'noopener,noreferrer',
		);
	}, [onTrack, view.globalViewUuid]);

	return (
		<MenuGroup minWidth={250} onClick={onClose}>
			<Section isList>
				{!isGlobalView && (
					<ButtonItem onClick={handleRename} isDisabled={!canRenameView || isReadOnly}>
						{formatMessage(renameAction)}
					</ButtonItem>
				)}
				{canRemoveEmoji && (
					<ButtonItem onClick={handleRemoveEmoji} isDisabled={!canManageView || isReadOnly}>
						{formatMessage(removeEmoji)}
					</ButtonItem>
				)}
				{(canSaveAsNewList || !fg('jpd_tree-view')) && (
					<HardLimitsTooltip containerId={containerId} entityLimitType={viewsEntityLimitType}>
						{({ isLimitReached }) => (
							<ButtonItem isDisabled={!canClone || isLimitReached} onClick={handleCloneTable}>
								{formatMessage(isGlobalView ? saveAsNewSpaceList : saveAsNewList)}
							</ButtonItem>
						)}
					</HardLimitsTooltip>
				)}
				{canSaveAsNewBoard && (
					<HardLimitsTooltip containerId={containerId} entityLimitType={viewsEntityLimitType}>
						{({ isLimitReached }) => (
							<ButtonItem isDisabled={!canClone || isLimitReached} onClick={handleCloneBoard}>
								{formatMessage(isGlobalView ? saveAsNewSpaceBoard : saveAsNewBoard)}
							</ButtonItem>
						)}
					</HardLimitsTooltip>
				)}
				{canSaveAsNewMatrix && (
					<HardLimitsTooltip containerId={containerId} entityLimitType={viewsEntityLimitType}>
						{({ isLimitReached }) => (
							<ButtonItem isDisabled={!canClone || isLimitReached} onClick={handleCloneMatrix}>
								{formatMessage(isGlobalView ? saveAsNewSpaceMatrix : saveAsNewMatrix)}
							</ButtonItem>
						)}
					</HardLimitsTooltip>
				)}
				{canSaveAsNewTimeline && (
					<HardLimitsTooltip containerId={containerId} entityLimitType={viewsEntityLimitType}>
						{({ isLimitReached }) => (
							<ButtonItem isDisabled={!canClone || isLimitReached} onClick={handleCloneTimeline}>
								{formatMessage(isGlobalView ? saveAsNewSpaceTimeline : saveAsNewTimeline)}
							</ButtonItem>
						)}
					</HardLimitsTooltip>
				)}
				{canSaveAsNewTree && fg('jpd_tree-view') && (
					<HardLimitsTooltip containerId={containerId} entityLimitType={viewsEntityLimitType}>
						{({ isLimitReached }) => (
							<ButtonItem isDisabled={!canClone || isLimitReached} onClick={handleCloneTree}>
								{formatMessage(saveAsNewTree)}
							</ButtonItem>
						)}
					</HardLimitsTooltip>
				)}
			</Section>
			{isGlobalView && isAdmin ? (
				<Section isList hasSeparator>
					<ButtonItem onClick={handleEditGlobalView} iconBefore={<LinkExternalIcon label="" />}>
						{formatMessage(editGlobalViewMessage)}
					</ButtonItem>
				</Section>
			) : (
				!isGlobalView && (
					<Section isList hasSeparator>
						<ButtonItem isDisabled={!canDeleteView} onClick={handleDelete}>
							{!canDeleteView ? (
								formatMessage(deleteAction)
							) : (
								<Text color="color.text.danger">{formatMessage(deleteAction)}</Text>
							)}
						</ButtonItem>
					</Section>
				)
			)}
			{isViewTemplateLibraryEnabled && (
				<Section hasSeparator>
					<ViewTemplatesButton
						onClick={handleViewTemplatesClick}
						projectId={navigationContainer.projectId}
						projectKey={navigationContainer.projectKey}
					/>
				</Section>
			)}
			{isBlueprintsButtonVisible && (
				<ViewBlueprintsButtonGuard>
					<Section hasSeparator>
						<ViewBlueprintsTriggerButton noIcon />
					</Section>
				</ViewBlueprintsButtonGuard>
			)}
			{isGlobalView && (
				<Box xcss={styles.adminNoticeContainer}>
					<Text size="small" color="color.text.subtle">
						{formatMessage(managedByAdmin)}
					</Text>
				</Box>
			)}
		</MenuGroup>
	);
};

export function Menu({ view, navigationContainer }: MenuProps): React.JSX.Element | null {
	const containerId = useContainerId();
	const { deleteView } = useNavigationActions();
	const containerTypePermissions = useContainerTypePermissions();
	const { loadViewPermissionsForCurrentUser } = useManageViewPermissions(
		containerTypePermissions === 'view' ? view.id : containerId,
	);
	const { sendMeatBallDropDownOnViewsMenuItemAnalyticsEvent } = useAnalyticsL4();
	const { sendMeatBallDropDownOnViewsAnalyticsEvent } = useAnalyticsL3();
	const { syncStateWithCollectionStoreAfterViewDelete } = useCollectionsActions();

	const trackMenuItemAnalyticsEvent = useCallback(
		(itemId: ItemId) => {
			sendMeatBallDropDownOnViewsMenuItemAnalyticsEvent({
				itemId,
				container: navigationContainer,
				viewParams: {
					viewKind: view.viewType,
					viewId: view.id,
				},
			});
		},
		[
			navigationContainer,
			sendMeatBallDropDownOnViewsMenuItemAnalyticsEvent,
			view.id,
			view.viewType,
		],
	);

	const [isDeleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);

	const viewPermissionsId = containerTypePermissions === 'view' ? view.id : containerId;

	const handleNavMenuOpen = useCallback(() => {
		sendMeatBallDropDownOnViewsAnalyticsEvent({
			container: navigationContainer,
			viewParams: {
				viewKind: view.viewType,
				viewId: view.id,
			},
		});
		loadViewPermissionsForCurrentUser(viewPermissionsId);
	}, [
		loadViewPermissionsForCurrentUser,
		navigationContainer,
		sendMeatBallDropDownOnViewsAnalyticsEvent,
		view.id,
		view.viewType,
		viewPermissionsId,
	]);

	const handleDelete = useCallback(() => {
		setDeleteConfirmationModalOpen(true);
	}, []);

	const handleDeleteModalCancel = useCallback(() => {
		setDeleteConfirmationModalOpen(false);
	}, []);

	const handleDeleteModalConfirm = useCallback(() => {
		setDeleteConfirmationModalOpen(false);
		deleteView(view.localId);
		syncStateWithCollectionStoreAfterViewDelete(containerId, view.id);
	}, [containerId, deleteView, syncStateWithCollectionStoreAfterViewDelete, view.id, view.localId]);

	const MenuItems = useCallback(
		({ onClose }: MoreNavMenuButtonMenuItemsProps) => (
			<NavMenuItems
				onClose={onClose}
				onDelete={handleDelete}
				onTrack={trackMenuItemAnalyticsEvent}
				view={view}
				navigationContainer={navigationContainer}
			/>
		),
		[handleDelete, navigationContainer, trackMenuItemAnalyticsEvent, view],
	);

	if (isFakeId(view.id)) {
		return null;
	}

	return (
		<>
			<MoreNavMenuButton onOpen={handleNavMenuOpen} MenuItems={MenuItems} name={view.name} />
			<DeleteModal
				title={view.name}
				isOpen={isDeleteConfirmationModalOpen}
				onCancel={handleDeleteModalCancel}
				onConfirm={handleDeleteModalConfirm}
			/>
		</>
	);
}
